import { ColorSwatchProps } from '@core-ui/color-swatch/src/types';
import {
  AdaptedProduct,
  Product,
  StyleColor,
} from '../../../collaborators/fetch-products/new-contract/types';
import priceAdapter from './price';
import imagesAdapter from '../images';

const getStringDefaultValueIfEmpty = (property: string): string => {
  return property || '';
};

const getArrayDefaultValueIfEmpty = (
  property: StyleColor[]
): StyleColor[] | [] => {
  return property || [];
};

const IsOutOfStock = (productColors: StyleColor[]): boolean =>
  !productColors.some((color) => {
    return color.inventoryCount > 0;
  });

const getImageURL = (
  categoryLargeImage: string,
  p1QuicklookImagePath: string
) => {
  return categoryLargeImage || p1QuicklookImagePath;
};

const getReviewStringValue = (value: number | null | undefined) =>
  value ? String(value) : '0';

// TODO: temporary mock value
const getMockValueIfEmpty = (imagePath: string, idx: number) =>
  imagePath
    ? imagePath
    : idx === 0
    ? 'https://www.gap.com/webcontent/0019/077/401/cn19077401.jpg'
    : 'https://www.gap.com/webcontent/0017/354/086/cn17354086.jpg';

const composeSwatchesProps = (
  cc: StyleColor,
  idx: number,
  productName: string
): ColorSwatchProps => {
  const {
    ccShortDescription,
    ccName,
    ccId,
    images,
    inventoryCount,
    vendorName,
    lowestPriceCC,
  } = cc;
  const adaptedImages = imagesAdapter(images);

  return {
    colorName: ccShortDescription,
    value: ccName,
    id: ccId,

    // below commented lines needs to be uncommented after testing url and productImage

    // url: getMockValueIfEmpty(adaptedImages.otherImages.colorSwatchImage, idx),
    // productImage: getMockValueIfEmpty(adaptedImages.avImages.p1QuicklookImagePath, idx),

    url: getMockValueIfEmpty('', idx),
    productImage: getMockValueIfEmpty('', idx),
    productImageAltText: productName
      ? `${productName} ${ccShortDescription}`
      : ccShortDescription,
    inStock: inventoryCount > 0,
    selected: idx === 0,
    vendorName: vendorName,
    isLowestPriceCC: lowestPriceCC,
  };
};

const prepareDropshipData = (product: Product) => {
  if (product?.webProductType === 'Dropship') {
    const { excludedFromPromotion, showSellerName, freeShipping } = product;
    const { lowestPriceCC, vendorName } = product.styleColors[0];
    return {
      dropshipData: {
        isExcludedFromPromotion: excludedFromPromotion,
        isShowSellerName: showSellerName,
        isFreeShipping: freeShipping,
        isLowestPriceCC: lowestPriceCC,
        vendorName: vendorName,
      },
    };
  }
};

const productAdapter = (product: Product): AdaptedProduct => {
  // TODO: preselect cc logic needed
  const selectedStyleColor = product.styleColors[0];
  const { avImages, otherImages, pristineImages, zoomImages } = imagesAdapter(
    selectedStyleColor?.images
  );
  const { categoryLargeImage, quicklookImage } = otherImages;
  const dropshipData = prepareDropshipData(product);

  return {
    altText: getStringDefaultValueIfEmpty(product.styleName),
    avImages,
    // The marketing flag name on the new contract comes in a array of strings
    // On this way, we can have more than one flag. So, here we are using the first one.
    // This can change on the future.
    marketingFlag: {
      marketingFlagName: getStringDefaultValueIfEmpty(
        product.styleMarketingFlags[0]
      ),
    },
    name: getStringDefaultValueIfEmpty(product.styleName),
    outOfStock: IsOutOfStock(getArrayDefaultValueIfEmpty(product.styleColors)),
    price: priceAdapter(product),
    pristineImages,
    zoomImages,
    categoryLargeImage: categoryLargeImage,
    quicklookImage: quicklookImage,
    productID: product.styleId,
    // No product URL received from pds
    productURL: '',
    reviewCount: getReviewStringValue(product.reviewCount),
    reviewScore: getReviewStringValue(product.reviewScore),
    swatchesProps: getArrayDefaultValueIfEmpty(product.styleColors).map(
      (color, idx) => composeSwatchesProps(color, idx, product.styleName)
    ),
    url: getImageURL(
      otherImages.categoryLargeImage,
      avImages.p1QuicklookImagePath
    ),
    //the vid property comes from the defaultSizeVariantId in the product
    //But it expects a number, and on the new contract it comes as a string
    // ex: TALL , REGULAR
    vid: selectedStyleColor?.defaultSizeVariantId,
    ...dropshipData,
  };
};

export default productAdapter;
